/**
 * 日落橙模版 - 详情页
 * 
 * 设计理念：桌面端左右分屏 + 信息面板卡片式堆叠
 * 与经典橙色（面包屑+大图+内容+侧栏）完全不同的排版：
 * - 桌面端：左侧大图（60%），右侧信息面板（40%）滚动
 * - 移动端：垂直布局，信息面板在图片下方
 * - 无Tab切换，所有信息一目了然
 * - 发布者+联系方式集成在一个紧凑卡片中
 * - 相关推荐：横向滚动卡片（非竖排列表）
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/templates/sunset/components/Navbar";
import SunsetFooter from "@/templates/sunset/components/Footer";
import { getListingDetail, getListings, type ListingDetail as ListingDetailType, type ListingItem } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function SunsetListingDetail() {
  const { id } = useParams<{ id: string }>();
  const { siteId, loading: siteLoading, siteConfig } = useSite();

  const [listing, setListing] = useState<ListingDetailType | null>(null);
  const [related, setRelated] = useState<ListingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!id || siteLoading || !siteId) return;
    setLoading(true);
    window.scrollTo(0, 0);
    getListingDetail(id)
      .then((data) => {
        setListing(data);
        document.title = data?.title ? `${data.title} - 详情` : '详情';
        if (data?.category_id) {
          getListings({ category_id: data.category_id, page_size: 6 })
            .then(d => setRelated((d.list || []).filter(i => i.id !== id).slice(0, 5)))
            .catch(() => {});
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, siteId, siteLoading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fffbf5]">
        <Navbar />
        <div className="pt-20 animate-pulse">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-3/5 aspect-[4/3] bg-gray-200 rounded-2xl" />
              <div className="md:w-2/5 space-y-4">
                <div className="h-8 bg-gray-200 rounded w-3/4" />
                <div className="h-5 bg-gray-200 rounded w-1/2" />
                <div className="h-32 bg-gray-200 rounded-2xl" />
                <div className="h-24 bg-gray-200 rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
        <SunsetFooter />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#fffbf5]">
        <Navbar />
        <div className="pt-28 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-3xl bg-amber-50">
            <i className="ri-error-warning-line text-3xl text-amber-400" />
          </div>
          <p className="text-gray-500 font-medium text-lg mb-2">信息不存在</p>
          <p className="text-gray-400 text-sm mb-6">该信息可能已被删除或下架</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-bold rounded-2xl hover:from-amber-600 hover:to-orange-700 transition-all shadow-md">
            <i className="ri-arrow-left-line" />返回首页
          </Link>
        </div>
        <SunsetFooter />
      </div>
    );
  }

  const images = listing.images || [];

  return (
    <div className="min-h-screen bg-[#fffbf5]">
      <Navbar />

      <div className="pt-20 pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">

          {/* ======== 面包屑 ======== */}
          <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-amber-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-amber-600 transition-colors">分类</Link>
            {listing.category?.name && (
              <>
                <i className="ri-arrow-right-s-line" />
                <Link to={`/category/${listing.category_id}`} className="hover:text-amber-600 transition-colors">
                  {listing.category.name}
                </Link>
              </>
            )}
          </nav>

          {/* ======== 第一行：标题 + 元信息 ======== */}
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight mb-3">
              {listing.title}
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              {listing.category?.name && (
                <span className="text-xs px-3 py-1 bg-amber-50 text-amber-700 rounded-full font-medium">
                  {listing.category.name}
                </span>
              )}
              {listing.is_pinned && (
                <span className="text-xs px-2.5 py-1 bg-amber-500 text-white rounded-full font-bold flex items-center gap-1">
                  <i className="ri-pushpin-line text-[10px]" />置顶
                </span>
              )}
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <i className="ri-time-line" />{formatDate(listing.created_at)}
              </span>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <i className="ri-eye-line" />{listing.view_count || 0} 次浏览
              </span>
              {listing.location && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-map-pin-line" />{listing.location}
                </span>
              )}
            </div>
          </div>

          {/* ======== 内容区域：桌面端双栏布局 ======== */}
          <div className="flex flex-col lg:flex-row gap-6">

            {/* ===== 左栏：内容 + 图片 ===== */}
            <div className="lg:flex-1 min-w-0 space-y-6">

              {/* 操作按钮 */}
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-500 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50 transition-all text-sm">
                  <i className="ri-share-forward-line" />分享
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-500 hover:text-rose-500 hover:border-rose-200 hover:bg-rose-50 transition-all text-sm">
                  <i className="ri-heart-line" />收藏
                </button>
              </div>

              {/* 内容描述 */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8">
                <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-1 h-5 bg-amber-500 rounded-full" />
                  详细描述
                </h3>
                {listing.content ? (
                  <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap break-words [&_a]:text-amber-600 [&_strong]:text-gray-800 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1">
                    {listing.content}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">暂无详细描述</p>
                )}

                {/* 标签 */}
                {listing.tags && listing.tags.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2">
                      {listing.tags.map((tag: string, i: number) => (
                        <span key={i} className="text-xs px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full font-medium">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 图片竖排展示（内容下方） */}
              {images.length > 0 && (
                <div className="space-y-3">
                  {images.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={listing.title}
                      className="w-full h-auto rounded-2xl cursor-pointer hover:opacity-95 transition-opacity"
                      onClick={() => { setCurrentImage(i); setLightboxOpen(true); }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ===== 右栏：联系信息（桌面端固定）===== */}
            <div className="lg:w-80 shrink-0">
              <div className="lg:sticky lg:top-24 space-y-5">

                {/* 发布者信息卡片 */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-md shadow-amber-200">
                      {listing.contact_name ? listing.contact_name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800">{listing.contact_name || "匿名用户"}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{formatDate(listing.created_at)} 发布</div>
                    </div>
                  </div>

                  {/* 公司名称 */}
                  {listing.company_name && !siteConfig?.hide_company_name && (
                    <div className="mb-4 p-3 bg-amber-50/50 rounded-xl">
                      <span className="text-xs text-gray-400">公司名称</span>
                      <p className="text-sm font-medium text-gray-700 mt-0.5">{listing.company_name}</p>
                    </div>
                  )}

                  {/* 联系方式列表 */}
                  <div className="space-y-2.5">
                    {listing.contact_phone && (
                      <a
                        href={`tel:${listing.contact_phone}`}
                        className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl hover:from-amber-100 hover:to-orange-100 transition-colors group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
                          <i className="ri-phone-line text-white" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">联系电话</div>
                          <div className="text-sm font-semibold text-gray-800 group-hover:text-amber-600 transition-colors">
                            {listing.contact_phone}
                          </div>
                        </div>
                      </a>
                    )}
                    {listing.contact_wechat && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                        <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
                          <i className="ri-wechat-line text-white" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">微信</div>
                          <div className="text-sm font-semibold text-gray-800">{listing.contact_wechat}</div>
                        </div>
                      </div>
                    )}
                    {listing.contact_email && (
                      <a
                        href={`mailto:${listing.contact_email}`}
                        className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl hover:from-blue-100 hover:to-cyan-100 transition-colors group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center shrink-0">
                          <i className="ri-mail-line text-white" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-400">电子邮箱</div>
                          <div className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                            {listing.contact_email}
                          </div>
                        </div>
                      </a>
                    )}
                    {!listing.contact_phone && !listing.contact_wechat && !listing.contact_email && (
                      <p className="text-gray-400 text-sm text-center py-3">暂未提供联系方式</p>
                    )}
                  </div>
                </div>

                {/* 快速操作 */}
                <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">快速操作</h4>
                  <Link
                    to="/publish"
                    className="flex items-center justify-center gap-2 w-full py-3 border-2 border-amber-200 text-amber-600 font-semibold rounded-xl hover:bg-amber-50 transition-all text-sm"
                  >
                    <i className="ri-add-line" />发布信息
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ======== 相关推荐：横向滚动 ======== */}
          {related.length > 0 && (
            <section className="mt-12">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-1 h-5 bg-amber-500 rounded-full" />
                  相关推荐
                </h2>
              </div>

              <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className="shrink-0 w-48 sm:w-56 group"
                  >
                    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-amber-200 transition-all">
                      <div className="aspect-square bg-amber-50 overflow-hidden">
                        {item.images?.[0] ? (
                          <img src={item.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-image-line text-amber-300 text-2xl" />
                          </div>
                        )}
                      </div>
                      <div className="p-3.5">
                        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-amber-600 transition-colors">
                          {item.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <span className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full">
                            {item.category?.name || "未分类"}
                          </span>
                          <span>{formatDate(item.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

        </div>
      </div>

      {/* ======== 图片灯箱 ======== */}
      {lightboxOpen && images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
          {images.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setCurrentImage((currentImage - 1 + images.length) % images.length); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}
          <img
            src={images[currentImage]}
            alt=""
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setCurrentImage((currentImage + 1) % images.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <i className="ri-arrow-right-s-line text-2xl" />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setCurrentImage(i); }}
                    className={`w-2 h-2 rounded-full transition-all ${i === currentImage ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ======== 移动端底部浮动联系按钮 ======== */}
      {listing.contact_phone && (
        <div className="fixed bottom-0 inset-x-0 md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 px-4 py-3 z-40">
          <div className="flex items-center gap-3">
            <a
              href={`tel:${listing.contact_phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-2xl shadow-lg shadow-amber-500/30"
            >
              <i className="ri-phone-line text-lg" />拨打电话
            </a>
            {listing.contact_wechat && (
              <button className="shrink-0 w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-600">
                <i className="ri-wechat-line text-lg" />
              </button>
            )}
          </div>
        </div>
      )}

      <SunsetFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
